import { useEffect, useState } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Dialog, MenuItem, Select, InputLabel, FormControl, Tooltip } from "@mui/material";
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDDatePicker from "components/MDDatePicker";

// Material Dashboard 2 React example components
import DataTable from "examples/Tables/DataTableV2";

function Create() {
    const { APIRequestType, fromDateParkFormAdm, postAPI, toDateParkFormAdm, setFromDateParkFormAdm, setParkFormInfosAdm, setToDateParkFormAdm } = useApiContext();

    const [formValues, setFormValues] = useState({});
    const [initialValues, setInitialValues] = useState({});
    const [error, setError] = useState(false); // Etat pour gerer l'erreur visuelle

    const [openDialog, setOpenDialog] = useState(false);

    const [parkPeriods, setParkPeriods] = useState([]);

    const [latitudeError, setLatitudeError] = useState(false);
    const [longitudeError, setLongitudeError] = useState(false);
    const [radiusError, setRadiusError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [emailError, setEmailError] = useState(false);

    const [contactsEnabled, setContactsEnabled] = useState([true, false, false]); // contact 1 actif par défaut

    useEffect(() => {
        const formData = {
            name: "",
            address: "",
            latitude: "",
            longitude: "",
            phone: "",
            email: "",
            phone2: "",
            email2: "",
            phone3: "",
            email3: "",
            radius: "",
            status: "",
        };

        setFormValues(formData);
        setInitialValues(formData);
    }, []);

    const isFormValid = () => {
        // Vérification des champs de base
        if (
            !formValues.name ||
            !formValues.address ||
            !formValues.latitude ||
            !formValues.longitude ||
            !formValues.radius ||
            !formValues.status ||
            latitudeError ||
            longitudeError ||
            radiusError
        ) {
            return false;
        }

        // Fonction de validation des contacts visibles
        const contactValid = (phoneKey, emailKey) => {
            const phone = formValues[phoneKey];
            const email = formValues[emailKey];
            return phone.length === 9 && isValidEmail(email);
        };

        // Vérifie contact principal
        if (!contactValid("phone", "email")) {
            return false;
        }

        // Vérifie contacts 2 et 3 si visibles
        if (contactsEnabled[1] && !contactValid("phone2", "email2")) {
            return false;
        }

        if (contactsEnabled[2] && !contactValid("phone3", "email3")) {
            return false;
        }

        return true;
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "name") {
            // Supprimer les caractères non alphabétiques (lettres accentuées, espaces, tirets autorisés)
            value = value.replace(/[^a-zA-ZÀ-ÿ0-9\s\-']/g, "");
            value = value.slice(0, 50);
            // Mettre toutes les lettres en majuscules
            value = value.toUpperCase();
        }

        if (field === "address") {
            // Autoriser lettres, chiffres, espaces, tirets
            value = value.replace(/[^a-zA-ZÀ-ÿ0-9\s\-']/g, "");
            value = value.slice(0, 50);
        }

        if (field === "latitude" || field === "longitude") {
            // Autoriser seulement chiffres, point, virgule, tiret
            value = value.replace(/[^0-9,.\-]/g, "");

            // Ne garder le '-' que s'il est en première position dans la chaîne d'origine
            const hasLeadingMinus = value.startsWith("-");
            value = value.replace(/-/g, ""); // Supprime tous les tirets
            if (hasLeadingMinus) {
                value = "-" + value; // Ne le remet que s'il était au début
            }

            // Remplacer virgule par point
            value = value.replace(",", ".");

            // Supprimer les zéros initiaux inutiles (sauf pour "0" ou "-0")
            const match = value.match(/^(-?)0+([1-9])/);
            if (match) {
                value = value.replace(/^(-?)0+/, "$1");
            }

            // Limiter à un seul point
            const parts = value.split(".");
            if (parts.length > 2) {
                value = parts[0] + "." + parts[1];
            }

            // Limiter à 5 chiffres après la virgule
            if (parts.length === 2) {
                parts[1] = parts[1].slice(0, 5);
                value = parts[0] + "." + parts[1];
            }

            // Vérification de la plage (si valeur convertible en float)
            const num = parseFloat(value);
            if (!isNaN(num)) {
                if (field === "latitude") {
                    setLatitudeError(num < -90 || num > 90);
                }

                if (field === "longitude") {
                    setLongitudeError(num < -180 || num > 180);
                }
            }
        }

        if (field === "phone") {
            // Supprimer tout sauf les chiffres
            value = value.replace(/\D/g, "");
            // Supprimer un éventuel 0 au début
            if (value.startsWith("0")) value = value.slice(1);
            // Limiter à 9 chiffres max
            value = value.slice(0, 9);
            // Mettre à jour l'erreur si ≠ 9 chiffres (uniquement s'il y a une saisie)
            setPhoneError(value.length > 0 && value.length !== 9);
        }

        if (field === "email") {
            value = value.trim(); // Supprime les espaces autour
            setEmailError(value.length > 0 && !isValidEmail(value));
        }

        if (field === "phone2") {
            value = value.replace(/\D/g, "");
            if (value.startsWith("0")) value = value.slice(1);
            value = value.slice(0, 9);
            setPhoneError(value.length > 0 && value.length !== 9);
        }

        if (field === "phone3") {
            value = value.replace(/\D/g, "");
            if (value.startsWith("0")) value = value.slice(1);
            value = value.slice(0, 9);
            setPhoneError(value.length > 0 && value.length !== 9);
        }

        if (field === "email2" || field === "email3") {
            value = value.trim();
            setEmailError(value.length > 0 && !isValidEmail(value));
        }

        if (field === "radius") {
            let num = parseFloat(value);

            if (value.length > 0) {
                if (isNaN(num) || num <= 0 || num > 1000) {
                    setRadiusError(true);
                } else {
                    setRadiusError(false);
                }
            } else {
                setRadiusError(true); //Erreur si la zone de texte est vide.
            }
        }

        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isValidEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleDeletePeriod = (indexToDelete) => {
        setParkPeriods((prev) => prev.filter((_, index) => index !== indexToDelete));
    };

    const handleReturnClick = () => {
        setParkFormInfosAdm({
            selectedPark: null,
            formType: "Tab",
        });
        console.log("Return PARK Tab");
    };

    const handleFromDateChange = (date) => {
        setFromDateParkFormAdm(date || null);
        if (date && toDateParkFormAdm && date > toDateParkFormAdm) {
            setToDateParkFormAdm(date);
        }
    };

    const handleToDateChange = (date) => {
        setToDateParkFormAdm(date || null);
    };

    const buildContactsArray = () => {
        const contacts = [];

        // Helper to determine visibility status
        const getVisibility = (enabled) => (enabled ? "visible" : "hide");

        // Contact 1 (Main Contact)
        contacts.push({
            id: null,
            state: getVisibility(true),
            phone: `0033${formValues.phone.replace(/^0/, "")}` || null, // Ajoute "0033" et enlève le 0 initial
            email: formValues.email || null,
        });

        // Contact 2
        contacts.push({
            id: null,
            state: getVisibility(contactsEnabled[1]),
            phone: `0033${formValues.phone2.replace(/^0/, "")}` || null,
            email: formValues.email2 || null,
        });

        // Contact 3
        contacts.push({
            id: null,
            state: getVisibility(contactsEnabled[2]),
            phone: `0033${formValues.phone3.replace(/^0/, "")}` || null,
            email: formValues.email3 || null,
        });

        return contacts;
    };

    const formatDateToDDMMYYYY = (dateObj) => {
        if (!dateObj || !dateObj.$d) return "Invalid date";
        const date = dateObj.$d;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };

    const handleCreateNewPark = () => {
        console.log("   parkPeriods BEFORE : ", parkPeriods);
        const formattedPeriods = parkPeriods
            .map(period => {
                const from = formatDateToDDMMYYYY(period.from);
                const to = formatDateToDDMMYYYY(period.to);
                return `[${from}, ${to}]`;
            })
            .join(', ');

        postAPI({
            requestType: APIRequestType.CREATE_NEW_PARK,
            name: formValues.name,
            address: formValues.address,
            position: `${Number(formValues.latitude).toFixed(5)}, ${Number(formValues.longitude).toFixed(5)}`,
            status: formValues.status,
            distance: formValues.radius,
            period: formattedPeriods || "[]",

            contacts: buildContactsArray(),
        });
    };

    return (
        <MDBox mt={5} pb={3}>
            <Grid container spacing={1}>
                <Grid item xs={24} md={12} xl={6} sx={{ display: "flex" }}>
                    <MDBox display="flex" flexDirection="column" width="100%" px={2}>
                        <MDBox mb={2} width="96%">
                            <MDInput
                                type="text"
                                label="Name"
                                fullWidth
                                value={formValues.name}
                                inputProps={{ color: "dark" }}
                                onChange={handleChange("name")}
                                error={error}
                                InputLabelProps={{ shrink: true }}
                                sx={{ borderRadius: "5px" }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="96%">
                            <MDInput
                                type="text"
                                label="Address"
                                fullWidth
                                value={formValues.address}
                                inputProps={{ color: "dark" }}
                                onChange={handleChange("address")}
                                error={error}
                                InputLabelProps={{ shrink: true }}
                                sx={{ borderRadius: "5px" }}
                            />
                        </MDBox>
                        <MDBox display="flex" width="96%" mb={2} justifyContent="space-between">
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="Latitude"
                                    fullWidth
                                    value={formValues.latitude}
                                    inputProps={{ color: "dark" }}
                                    onChange={handleChange("latitude")}
                                    error={latitudeError}
                                    helperText={latitudeError ? "Value out of range." : ""}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ borderRadius: "5px" }}
                                />
                            </MDBox>
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="Longitude"
                                    fullWidth
                                    value={formValues.longitude}
                                    inputProps={{ color: "dark" }}
                                    onChange={handleChange("longitude")}
                                    error={longitudeError}
                                    helperText={longitudeError ? "Value out of range." : ""}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ borderRadius: "5px" }}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" ml={1.5} mt={2} mb={2} width="94%">
                            <MDBox display="flex" alignItems="center">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    color="dark"
                                    fontSize="0.9rem"
                                >
                                    {"MAIN Contact"}
                                </MDTypography>
                                <Tooltip
                                    title="Alerts associated with this park will be notified to one or several contacts."
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "#d8dadd",
                                                color: "#344767",
                                                fontSize: "0.85rem",
                                                fontWeight: 'bold',
                                                borderRadius: 3,
                                            },
                                        },
                                        arrow: {
                                            sx: {
                                                color: "#d8dadd",
                                            },
                                        },
                                    }}
                                >
                                    <Icon color="secondary" fontSize="small" sx={{ ml: 1 }}>
                                        info_outline
                                    </Icon>
                                </Tooltip>
                            </MDBox>

                            {/* Affiche le bouton ADD CONTACT seulement si < 3 contacts */}
                            {contactsEnabled.filter(Boolean).length < 3 && (
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => {
                                        const nextIndex = contactsEnabled.findIndex(enabled => !enabled);
                                        if (nextIndex !== -1) {
                                            const newState = [...contactsEnabled];
                                            newState[nextIndex] = true;
                                            setContactsEnabled(newState);
                                        }
                                    }}
                                    sx={{ height: "30px", fontSize: "0.75rem" }}
                                >
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon> ADD ADDITIONAL CONTACT
                                </MDButton>
                            )}
                        </MDBox>
                        <MDBox mb={2} width="40%">
                            <MDInput
                                type="tel"
                                label="Phone to Notify"
                                fullWidth
                                value={formValues.phone}
                                onChange={handleChange("phone")}
                                error={phoneError}
                                helperText={phoneError ? "Invalid Format." : ""}
                                inputProps={{ color: "dark", maxLength: 9 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                +33
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ borderRadius: "5px" }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="70%">
                            <MDInput
                                type="email"
                                label="Email to Notify"
                                fullWidth
                                value={formValues.email}
                                inputProps={{ color: "dark" }}
                                onChange={handleChange("email")}
                                error={emailError}
                                helperText={emailError ? "Invalid Format." : ""}
                                InputLabelProps={{ shrink: true }}
                                sx={{ borderRadius: "5px" }}
                            />
                        </MDBox>
                        {/* Additional Contact 2 */}
                        {contactsEnabled[1] && (
                            <>
                                <MDBox mt={2.5} ml={1.5} mb={1.5} display="flex" justifyContent="space-between" alignItems="center" width="94%">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        color="dark"
                                        fontSize="0.9rem"
                                    >
                                        {"Additional Contact"}
                                    </MDTypography>
                                    <MDButton
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            const newState = [...contactsEnabled];
                                            newState[1] = false;
                                            setContactsEnabled(newState);
                                            setFormValues((prev) => ({ ...prev, phone2: "", email2: "" }));
                                        }}
                                        sx={{ ml: 2, height: "30px", fontSize: "0.75rem" }}
                                    >
                                        <Icon fontSize="small" sx={{ mr: 0.5 }}>delete</Icon> DELETE
                                    </MDButton>
                                </MDBox>

                                <MDBox mb={2} width="40%">
                                    <MDInput
                                        type="tel"
                                        label="Phone to Notify"
                                        fullWidth
                                        value={formValues.phone2}
                                        onChange={handleChange("phone2")}
                                        error={phoneError}
                                        helperText={phoneError ? "Invalid Format." : ""}
                                        inputProps={{ color: "dark", maxLength: 9 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                        +33
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ borderRadius: "5px" }}
                                    />
                                </MDBox>

                                <MDBox mb={2} width="70%">
                                    <MDInput
                                        type="email"
                                        label="Email to Notify"
                                        fullWidth
                                        value={formValues.email2}
                                        inputProps={{ color: "dark" }}
                                        onChange={handleChange("email2")}
                                        error={emailError}
                                        helperText={emailError ? "Invalid Format." : ""}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ borderRadius: "5px" }}
                                    />
                                </MDBox>
                            </>
                        )}

                        {/* Additional Contact 3 */}
                        {contactsEnabled[2] && (
                            <>
                                <MDBox mt={2.5} ml={1.5} mb={1.5} display="flex" justifyContent="space-between" alignItems="center" width="94%">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        color="dark"
                                        fontSize="0.9rem"
                                    >
                                        {"Additional Contact"}
                                    </MDTypography>
                                    <MDButton
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            const newState = [...contactsEnabled];
                                            newState[2] = false;
                                            setContactsEnabled(newState);
                                            setFormValues((prev) => ({ ...prev, phone3: "", email3: "" }));
                                        }}
                                        sx={{ ml: 2, height: "30px", fontSize: "0.75rem" }}
                                    >
                                        <Icon fontSize="small" sx={{ mr: 0.5 }}>delete</Icon> DELETE
                                    </MDButton>
                                </MDBox>

                                <MDBox mb={2} width="40%">
                                    <MDInput
                                        type="tel"
                                        label="Phone to Notify"
                                        fullWidth
                                        value={formValues.phone3}
                                        onChange={handleChange("phone3")}
                                        error={phoneError}
                                        helperText={phoneError ? "Invalid Format." : ""}
                                        inputProps={{ color: "dark", maxLength: 9 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                        +33
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ borderRadius: "5px" }}
                                    />
                                </MDBox>

                                <MDBox mb={2} width="70%">
                                    <MDInput
                                        type="email"
                                        label="Email to Notify"
                                        fullWidth
                                        value={formValues.email3}
                                        inputProps={{ color: "dark" }}
                                        onChange={handleChange("email3")}
                                        error={emailError}
                                        helperText={emailError ? "Invalid Format." : ""}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ borderRadius: "5px" }}
                                    />
                                </MDBox>
                            </>
                        )}
                    </MDBox>
                </Grid>
                <Grid item xs={24} md={12} xl={5.8}>
                    <MDBox pt={3} pb={2.5}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Card>
                                    <MDBox
                                        mx={2}
                                        mt={-3}
                                        py={1.5}
                                        px={2}
                                        variant="gradient"
                                        bgColor="info"
                                        borderRadius="lg"
                                        coloredShadow="info"
                                    >
                                        <MDTypography variant="h6" color="white">
                                            Bridage Agricole Periods
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox pt={3}>
                                        <MDBox display="flex" justifyContent="flex-end" mb={1.5}>
                                            <MDButton
                                                onClick={() => setOpenDialog(true)}
                                                sx={{
                                                    mb: -3,
                                                    mr: 0.5,
                                                    height: "45px",
                                                    '&:hover': {
                                                        backgroundColor: "#f0f2f5",
                                                    },
                                                }}
                                            >
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <MDTypography variant="h7" color="secondary" fontWeight="medium">
                                                        ADD NEW PERIOD
                                                    </MDTypography>
                                                    <Icon color="secondary" fontSize="large">add</Icon>
                                                </MDBox>
                                            </MDButton>
                                        </MDBox>
                                        <DataTable
                                            table={{
                                                columns: [
                                                    { Header: "", accessor: "name", align: "left" },
                                                    { Header: "", accessor: "icon", align: "right" },
                                                ],
                                                rows: parkPeriods.length === 0
                                                    ? [{
                                                        nameValue: "...",
                                                        name: (
                                                            <MDTypography component="span" variant="caption" color="dark" fontWeight="medium">
                                                                ...
                                                            </MDTypography>
                                                        ),
                                                    }]
                                                    : parkPeriods.map((period, index) => {
                                                        const formatDate = (dateStr) => {
                                                            console.log("    dateStr : ", dateStr);
                                                            // Vérifier si dateStr est un objet et contient une propriété $d de type Date
                                                            if (dateStr && dateStr.$d instanceof Date) {
                                                                // Extraire l'objet Date de $d
                                                                const date = dateStr.$d;

                                                                // Convertir la date en chaîne locale au format "DD-MM-YYYY"
                                                                const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
                                                                const dateFormatted = date.toLocaleDateString('fr-FR', options); // Format français : "DD/MM/YYYY"

                                                                // Séparer les parties de la date : jour, mois, année
                                                                const [dd, mm, yyyy] = dateFormatted.split("/");

                                                                return `${yyyy}-${mm}-${dd}`; // Retourner la date au format "YYYY-MM-DD"
                                                            } else {
                                                                // Si dateStr n'est pas au bon format, retourner une valeur par défaut
                                                                return "Invalid date.";
                                                            }
                                                        };

                                                        return {
                                                            nameValue: `From ${period.from} to ${period.to}`,
                                                            name: (
                                                                <MDTypography component="span" variant="caption" color="dark" fontWeight="medium">
                                                                    [ {formatDate(period.from)}, {formatDate(period.to)} ]
                                                                </MDTypography>
                                                            ),
                                                            icon: (
                                                                <MDBox>
                                                                    <Icon
                                                                        fontSize="small"
                                                                        sx={{ mt: -0.25, '&:hover': { color: "#b7e4fd" } }}
                                                                        onClick={() => handleDeletePeriod(index)}
                                                                    >
                                                                        delete
                                                                    </Icon>
                                                                </MDBox>
                                                            ),
                                                        };
                                                    }),
                                            }}
                                            rowsNumberPerPage={5}
                                            isSorted={false}
                                            entriesPerPage={false}
                                            showTotalEntries={false}
                                            noEndBorder
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                    <MDBox display="flex" width="30%" justifyContent="space-between" alignItems="flex-start" ml={1.5} mt={2.5} mb={2}>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="dark"
                            fontSize="0.9rem"
                        >
                            {"Park Specification"}
                        </MDTypography>
                    </MDBox>
                    <MDBox display="flex" width="40%" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <MDInput
                            type="text"
                            label="Observation Radius (m)"
                            fullWidth
                            value={formValues.radius}
                            inputProps={{ color: "dark" }}
                            onChange={handleChange("radius")}
                            error={radiusError}
                            helperText={radiusError ? "Choose between 0 & 1000." : ""}
                            InputLabelProps={{ shrink: true }}
                            sx={{ borderRadius: "5px" }}
                        />
                        <Tooltip
                            title="This is the observation radius surrounding the wind turbine."
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "#d8dadd",
                                        color: "#344767",
                                        fontSize: "0.85rem",
                                        fontWeight: 'bold',
                                        borderRadius: 3,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: "#d8dadd",
                                    },
                                },
                            }}
                        >
                            <Icon
                                color="secondary"
                                fontSize="small"
                                sx={{ ml: 1, mt: 1 }}>
                                info_outline
                            </Icon>
                        </Tooltip>
                    </MDBox>

                    <MDBox mb={2} width="100%">
                        <FormControl fullWidth>
                            <Select
                                value={formValues.status || ""}
                                onChange={(e) => setFormValues((prev) => ({
                                    ...prev,
                                    status: e.target.value,
                                }))}
                                displayEmpty
                                error={error}
                                sx={{
                                    borderRadius: "5px",
                                    minHeight: "45px",
                                }}
                                inputProps={{ color: "dark" }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return (
                                            <span style={{ color: "#9e9e9e" }}>
                                                Status...
                                            </span>
                                        );
                                    }
                                    return (
                                        <span style={{ color: "#344767" }}>
                                            {selected}
                                        </span>
                                    );
                                }}
                            >
                                {/* Placeholder */}
                                <MenuItem value="" disabled>
                                    <span style={{ color: "#9e9e9e" }}>
                                        Status...
                                    </span>
                                </MenuItem>

                                {/* Liste des rôles */}
                                {["ACTIVE", "IN MAINTENANCE", "INACTIVE"].map((role) => (
                                    <MenuItem key={role} value={role}>
                                        <span style={{ color: "dark" }}>{role}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </MDBox>
                </Grid>
            </Grid>
            <Grid item xs={24} md={12} xl={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <MDBox display="flex" flexDirection="row" gap={3} justifyContent="center" alignItems="center">
                    <MDBox mt={1} flexGrow={1}>
                        <MDButton
                            type="submit"
                            variant="gradient"
                            color="info"
                            onClick={handleCreateNewPark}
                            fullWidth
                            disabled={!isFormValid()}
                            sx={{ width: '150px' }}
                        >
                            CREATE
                        </MDButton>
                    </MDBox>
                    <MDBox mt={1} flexGrow={1}>
                        <MDButton
                            type="submit"
                            variant="outlined"
                            color="secondary"
                            onClick={handleReturnClick}
                            fullWidth
                            sx={{ width: '150px' }}
                        >
                            CANCEL
                        </MDButton>
                    </MDBox>
                </MDBox>
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
                <MDBox px={3} py={2} position="relative">
                    {/* Close button */}
                    <Icon
                        sx={{ position: "absolute", right: 15, top: 15, cursor: "pointer" }}
                        onClick={() => setOpenDialog(false)}
                    >
                        close
                    </Icon>

                    <MDTypography variant="h6" fontWeight="medium" mb={2} textAlign="center" width="100%">
                        Add New Period
                    </MDTypography>

                    <MDBox display="flex" alignItems="center" gap={0}>
                        {/* Date Picker "From" */}
                        <MDDatePicker
                            label="From"
                            value={fromDateParkFormAdm}
                            onChange={handleFromDateChange}
                            scale={0.9}
                        />

                        {/* Date Picker "To" */}
                        <MDDatePicker
                            label="To"
                            value={toDateParkFormAdm}
                            onChange={handleToDateChange}
                            minDate={fromDateParkFormAdm} // Empêche de choisir une date antérieure
                            scale={0.9}
                        />
                    </MDBox>

                    <MDBox display="flex" justifyContent="center" mt={3}>
                        <MDButton
                            variant="gradient"
                            color="info"
                            disabled={!fromDateParkFormAdm || !toDateParkFormAdm}
                            onClick={() => {
                                setParkPeriods((prev) => [...prev, { from: fromDateParkFormAdm, to: toDateParkFormAdm }]);
                                setFromDateParkFormAdm(null);
                                setToDateParkFormAdm(null);
                                setOpenDialog(false);
                            }}
                        >
                            Add
                        </MDButton>
                    </MDBox>
                </MDBox>
            </Dialog>
        </MDBox>
    );
}

export default Create;